// Lee las métricas de Instagram de una marca por la edge `ig-metricas`. El token
// nunca pasa por acá. Ver ADR-2026-09-24-instagram-metricas.

import { supabase } from "./supabase";
import { EVENTO_CAMBIO_DE_INSTAGRAM } from "./instagramConexionLogica";

export type AccionDeMetricas = "cuenta" | "posts" | "post" | "publico";

export interface PedidoDeMetricas {
  accion: AccionDeMetricas;
  dias?: number | null;
  orden?: string;
  cantidad?: number;
  post_id?: string;
}

/**
 * Lo que devuelve la edge. `conectada`/`disponible` + `mensaje` cuando el problema
 * es de la cuenta (vencida, sin permiso, Meta pidió esperar); el resto depende de
 * la acción y lo interpreta `chat-atlas/instagramEnElChat.ts`.
 */
export interface RespuestaDeMetricas {
  conectada: boolean;
  disponible?: boolean;
  estado?: string;
  mensaje?: string;
  [clave: string]: unknown;
}

export async function consultarMetricasDeInstagram(
  brandId: string | null,
  pedido: PedidoDeMetricas,
): Promise<RespuestaDeMetricas> {
  const { data, error } = await supabase.functions.invoke("ig-metricas", {
    body: { ...pedido, brand_id: brandId },
  });
  if (error) throw new Error(error.message || "No se pudieron leer las métricas de Instagram.");
  return data as RespuestaDeMetricas;
}

// ── El dashboard de métricas (ADR-2026-10-29-dashboard-de-metricas-de-instagram) ──────────────
//
// La pantalla lee de la misma edge que el chat, en vivo: no hay tablas ni barrido todavía. Para no
// gastar el cupo de consultas que Meta da por cuenta, lo leído se guarda unos minutos en memoria y
// dos pedidos iguales al mismo tiempo comparten la llamada.

/** Un post de Instagram como lo resume la edge (`resumirPost`). */
export interface PostDeInstagram {
  id: string;
  fecha: string | null;
  /** "reel", "post", "historia" o "anuncio". */
  tipo: string;
  /** "imagen", "video" o "carrusel". */
  formato: string;
  texto: string;
  link: string | null;
  /** Del CDN de Instagram: vence, se usa al momento. */
  miniatura: string | null;
  me_gusta: number | null;
  comentarios: number | null;
  alcance: number | null;
  vistas: number | null;
  guardados: number | null;
  compartidos: number | null;
  interacciones: number | null;
  segundos_promedio_vistos: number | null;
}

export interface MetricaDeLaCuenta {
  metrica: string;
  nombre: string;
  valor: number | null;
  anterior: number | null;
  variacion_pct: number | null;
}

export interface CuentaDeInstagram {
  seguidores: number | null;
  publicaciones: number | null;
  metricas: MetricaDeLaCuenta[];
  dias: number;
}

/** Lo que la pantalla necesita saber de una lectura: los datos, o por qué no hay. */
export type Lectura<T> =
  | { tipo: "ok"; datos: T; usuario: string | null }
  | { tipo: "sin_conexion" }
  | { tipo: "vencida"; mensaje: string }
  /** `sinPermiso`: se conectó sin aceptar el de métricas; reintentar no sirve, hay que reconectar. */
  | { tipo: "no_disponible"; mensaje: string; sinPermiso: boolean }
  /** El post se borró en Instagram (o es de otra cuenta que la conectada). */
  | { tipo: "borrado" };

const SIN_MENSAJE = "Instagram no devolvió los números. Probá de nuevo en un rato.";

/** Traduce la respuesta de la edge. `extraer` arma los datos cuando vino todo bien. */
export function leerRespuesta<T>(r: RespuestaDeMetricas, extraer: (r: RespuestaDeMetricas) => T): Lectura<T> {
  if (!r || r.conectada !== true) return { tipo: "sin_conexion" };
  if (r.borrado === true) return { tipo: "borrado" };
  const mensaje = typeof r.mensaje === "string" && r.mensaje ? r.mensaje : SIN_MENSAJE;
  if (r.estado === "vencida") return { tipo: "vencida", mensaje };
  if (r.disponible === false) return { tipo: "no_disponible", mensaje, sinPermiso: r.permiso === false };
  return { tipo: "ok", datos: extraer(r), usuario: typeof r.usuario === "string" ? r.usuario : null };
}

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

export function extraerCuenta(r: RespuestaDeMetricas): CuentaDeInstagram {
  const cuenta = (r.cuenta ?? {}) as Record<string, unknown>;
  const periodo = (r.periodo ?? {}) as Record<string, unknown>;
  return {
    seguidores: num(cuenta.seguidores),
    publicaciones: num(cuenta.publicaciones),
    metricas: Array.isArray(r.metricas) ? (r.metricas as MetricaDeLaCuenta[]) : [],
    dias: num(periodo.dias) ?? 28,
  };
}

export function extraerPosts(r: RespuestaDeMetricas): PostDeInstagram[] {
  return Array.isArray(r.posts) ? (r.posts as PostDeInstagram[]) : [];
}

export function extraerPost(r: RespuestaDeMetricas): PostDeInstagram | null {
  return r.post && typeof r.post === "object" ? (r.post as PostDeInstagram) : null;
}

/** Cuánto se usa lo ya leído antes de volver a preguntarle a Instagram. */
export const VIGENCIA_MS = 15 * 60_000;

interface EnMemoria {
  leidoEn: number;
  valor: Lectura<unknown>;
}
const memoria = new Map<string, EnMemoria>();
const enVuelo = new Map<string, Promise<Lectura<unknown>>>();
/** Sube cada vez que se olvida todo: una lectura que salió antes no se guarda después. */
let generacion = 0;

/** Al conectar, reconectar o desconectar Instagram (y en los tests): lo leído ya no vale. */
export function olvidarMetricasLeidas() {
  generacion++;
  memoria.clear();
  enVuelo.clear();
}
try {
  window.addEventListener(EVENTO_CAMBIO_DE_INSTAGRAM, olvidarMetricasLeidas);
} catch {
  // Sin window (tests de Node): no hay conexión que cambie.
}

async function leerConMemoria<T>(
  brandId: string | null,
  pedido: PedidoDeMetricas,
  extraer: (r: RespuestaDeMetricas) => T,
  opciones: { forzar?: boolean; consultar?: typeof consultarMetricasDeInstagram; ahora?: () => number } = {},
): Promise<{ lectura: Lectura<T>; leidoEn: number }> {
  const ahora = opciones.ahora ?? Date.now;
  const clave = JSON.stringify([brandId, pedido]);
  const guardado = memoria.get(clave);
  if (!opciones.forzar && guardado && ahora() - guardado.leidoEn < VIGENCIA_MS) {
    return { lectura: guardado.valor as Lectura<T>, leidoEn: guardado.leidoEn };
  }
  const deEstaConexion = generacion;
  let promesa = enVuelo.get(clave) as Promise<Lectura<T>> | undefined;
  if (!promesa) {
    const consultar = opciones.consultar ?? consultarMetricasDeInstagram;
    promesa = consultar(brandId, pedido).then((r) => leerRespuesta(r, extraer));
    enVuelo.set(clave, promesa as Promise<Lectura<unknown>>);
  }
  try {
    const lectura = await promesa;
    const leidoEn = ahora();
    // Sólo se recuerda lo que salió bien: un "vencida" o un error se vuelve a mirar la próxima vez.
    // Si en el medio se conectó otra cuenta (o se desconectó), esto es de la anterior: no se guarda.
    const vigente = deEstaConexion === generacion;
    if (vigente && (lectura.tipo === "ok" || lectura.tipo === "borrado")) memoria.set(clave, { leidoEn, valor: lectura });
    return { lectura, leidoEn };
  } finally {
    // Sólo si sigue siendo la propia: un "Actualizar" pudo haber dejado otra en vuelo.
    if (enVuelo.get(clave) === promesa) enVuelo.delete(clave);
  }
}

type OpcionesDeLectura = Parameters<typeof leerConMemoria>[3];

/** El resumen de la cuenta en los últimos `dias`, comparado con el período anterior. */
export function leerCuentaDeInstagram(brandId: string | null, dias: number, opciones?: OpcionesDeLectura) {
  return leerConMemoria(brandId, { accion: "cuenta", dias }, extraerCuenta, opciones);
}

/** Los últimos 30 posts con sus números (el tope de la edge). */
export function leerPostsDeInstagram(brandId: string | null, opciones?: OpcionesDeLectura) {
  return leerConMemoria(brandId, { accion: "posts", orden: "recientes", cantidad: 30 }, extraerPosts, opciones);
}

/** Un post (el de "Cómo le fue" en el calendario). */
export function leerPostDeInstagram(brandId: string | null, mediaId: string, opciones?: OpcionesDeLectura) {
  return leerConMemoria(brandId, { accion: "post", post_id: mediaId }, extraerPost, opciones);
}
